import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import Breadcrumbs from '../components/Breadcrumbs'

export default function AboutPage() {
  const { t } = useTranslation()
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 })

  return (
    <div className="pb-16">
      <Breadcrumbs items={[{ key: 'about' }]} />
      {/* Hero */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              {t('about.title')}
            </h1>
            <p className="text-xl text-slate-600 dark:text-slate-400">
              {t('about.pageSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main content */}
      <section className="py-16 md:py-24 bg-white dark:bg-slate-900" ref={ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 dark:bg-slate-800 rounded-[2rem] sm:rounded-[3rem] p-8 sm:p-12 lg:p-20 overflow-hidden relative">
            <div className="relative z-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">
                  {t('about.visionTitle')}
                </h2>
                <div className="space-y-6 text-base sm:text-lg text-slate-600 dark:text-slate-400">
                  <p>{t('about.p1')}</p>
                  <p>{t('about.p2')}</p>
                  <p>{t('about.p3')}</p>
                </div>
                <div className="flex flex-wrap items-center gap-6 pt-6 sm:pt-8">
                  <div className="text-center">
                    <p className="text-primary text-2xl sm:text-3xl font-bold">15+</p>
                    <p className="text-xs sm:text-sm font-semibold uppercase text-slate-500">
                      {t('about.expertise')}
                    </p>
                  </div>
                  <div className="w-px h-10 sm:h-12 bg-slate-200 dark:bg-slate-700 hidden sm:block" />
                  <div className="text-center">
                    <p className="text-primary text-2xl sm:text-3xl font-bold">100%</p>
                    <p className="text-xs sm:text-sm font-semibold uppercase text-slate-500">
                      {t('about.independent')}
                    </p>
                  </div>
                  <div className="w-px h-10 sm:h-12 bg-slate-200 dark:bg-slate-700 hidden sm:block" />
                  <div className="text-center">
                    <p className="text-primary text-2xl sm:text-3xl font-bold">24/7</p>
                    <p className="text-xs sm:text-sm font-semibold uppercase text-slate-500">
                      {t('about.support')}
                    </p>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <img
                  alt="Team meeting"
                  className="rounded-2xl sm:rounded-3xl shadow-2xl w-full object-cover rotate-2 hover:rotate-0 transition-transform duration-500"
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuCLDjtA1wz-oI7S7WnsDO0BaW5auorwVDVM87PbcPLI8slgWpaTR4vupfRlk8shqe38jW3uDkyDasEsAwF2aCKPhtODTOOy--TinAAKxy338Ao1wdat-aNqmQPhaZCv6ziOgrTkstNxnkbeuFlFDZkIEZzkuBHjUWvkLxJPJvDqZqcjB2bjiCrOPRE_2usAh7bQaIcFoFPTEl7BmLfRhgB57TFfhtzQzgvVyJb2-rdMrH60Je_iwLOT4tECci7pF0exCzSBUUTjqw"
                />
              </motion.div>
            </div>
            <div className="absolute top-0 right-0 w-48 h-48 sm:w-64 sm:h-64 bg-primary/5 rounded-full -mr-24 -mt-24 sm:-mr-32 sm:-mt-32" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">{t('contact.title')}</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-8 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-primary text-white rounded-xl font-bold text-lg hover:scale-105 transition-transform shadow-xl shadow-primary/30"
            >
              {t('contact.ctaExpert')}
            </Link>
            <Link
              to="/solutions"
              className="inline-block px-8 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              {t('hero.ctaPrimary')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
